/**
 * AdminSubscriptionsTab - Manage user subscriptions.
 *
 * Search for users, view their current subscription,
 * change their plan, or cancel their subscription.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, UserCircle, ArrowRight, XCircle } from 'lucide-react';
import {
  getUsers,
  getPlans,
  changeUserPlan,
  cancelUserSubscription,
  type AdminUserData,
  type PlanData,
} from '@/services/adminService';

const AdminSubscriptionsTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AdminUserData[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUserData | null>(null);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getPlans().then(({ plans }) => setPlans(plans)).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError('');
    setSelectedUser(null);
    try {
      const { users } = await getUsers({ search: searchQuery, limit: '10' });
      setSearchResults(users);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user: AdminUserData) => {
    setSelectedUser(user);
    setSelectedPlanId('');
    setSuccess('');
  };

  const handleChangePlan = async () => {
    if (!selectedUser || !selectedPlanId) return;
    setError('');
    setSuccess('');
    try {
      await changeUserPlan(selectedUser.id, selectedPlanId);
      setSuccess(`Plano alterado com sucesso para ${selectedUser.email}`);
      // Refresh the user's data
      const { users } = await getUsers({ search: selectedUser.email, limit: '1' });
      if (users.length > 0) {
        setSelectedUser(users[0]);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCancel = async () => {
    if (!selectedUser?.subscription?.id) return;
    setError('');
    setSuccess('');
    try {
      await cancelUserSubscription(selectedUser.subscription.id);
      setSuccess(`Assinatura cancelada para ${selectedUser.email}`);
      // Refresh
      const { users } = await getUsers({ search: selectedUser.email, limit: '1' });
      if (users.length > 0) {
        setSelectedUser(users[0]);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="bg-green-500/10 text-green-600 dark:text-green-400 text-sm p-3 rounded-lg">{success}</div>
      )}

      <h2 className="text-lg font-semibold">Gerenciar Assinaturas</h2>

      {/* Search */}
      <div className="flex gap-2">
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Buscar por email ou nome..."
          className="flex-1"
        />
        <Button onClick={handleSearch} disabled={loading}>
          <Search className="h-4 w-4 mr-1" />
          {loading ? 'Buscando...' : 'Buscar'}
        </Button>
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="space-y-2">
          {searchResults.map((user) => (
            <button
              key={user.id}
              onClick={() => handleSelectUser(user)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                selectedUser?.id === user.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <UserCircle className="h-8 w-8 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.display_name || user.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-medium">
                    {user.subscription?.planName || 'Sem plano'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected user detail */}
      {selectedUser && (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <div>
            <h3 className="font-medium">{selectedUser.display_name || selectedUser.email}</h3>
            <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Plano atual:</span>
              <span className="ml-2 font-medium">
                {selectedUser.subscription?.planName || 'Sem plano'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <span className="ml-2 font-medium">
                {selectedUser.subscription?.status || 'sem assinatura'}
              </span>
            </div>
          </div>

          {/* Change plan */}
          <div className="flex items-center gap-2">
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Selecionar plano...</option>
              {plans.filter((p) => p.isActive).map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} ({plan.slug}){!plan.isPublic ? ' [Interno]' : ''}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={handleChangePlan} disabled={!selectedPlanId}>
              <ArrowRight className="h-4 w-4 mr-1" /> Alterar Plano
            </Button>
          </div>

          {/* Cancel subscription */}
          {selectedUser.subscription && ['active', 'ACTIVE', 'TRIALING'].includes(selectedUser.subscription.status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              className="text-destructive hover:text-destructive"
            >
              <XCircle className="h-4 w-4 mr-1" /> Cancelar Assinatura
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionsTab;
